import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { IndianRupee, Eye, Sparkles, Check } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { PreviewModal } from "./PreviewModal";

export const ProductCard = ({ product, onBuy, index = 0 }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  return (
    <motion.div
      data-testid={`product-card-${product.slug}`}
      initial={{ y: 40 }}
      whileInView={{ y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
      className="group flex flex-col overflow-hidden rounded-[2rem] bg-white shadow-xl shadow-blue-900/5 transition-transform duration-300 hover:-translate-y-2"
    >
      <Link
        to={`/product/${product.slug}`}
        className="relative flex h-64 items-center justify-center overflow-hidden"
        style={{ backgroundColor: `${product.accent}14` }}
      >
        <img
          src={imgUrl(product.cover_image)}
          alt={product.name}
          loading="lazy"
          className="h-full w-auto max-w-full object-contain p-4 drop-shadow-lg transition-transform duration-500 group-hover:scale-[1.04] group-hover:-rotate-1"
        />
        <span
          className="absolute left-4 top-4 rounded-full px-4 py-1.5 font-display text-sm font-bold text-white"
          style={{ backgroundColor: product.accent }}
        >
          Ages {product.ages}
        </span>
        {product.is_new && (
          <span className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-brand-purple px-3 py-1.5 font-display text-xs font-bold text-white">
            <Sparkles className="h-3.5 w-3.5" /> New
          </span>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-6 sm:p-7">
        <p
          className="font-display text-xs font-bold uppercase tracking-widest"
          style={{ color: product.accent }}
        >
          {product.tagline}
        </p>
        <Link to={`/product/${product.slug}`}>
          <h3 className="mt-1.5 font-display text-xl font-bold text-slate-800 transition-colors hover:text-brand-blue sm:text-2xl">
            {product.name}
          </h3>
        </Link>
        <p className="mt-2 flex-1 text-sm font-semibold leading-relaxed text-slate-500">
          {product.description}
        </p>
        {product.learn?.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {product.learn.slice(0, 3).map((b) => (
              <li key={b} className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                <Check className="h-4 w-4 shrink-0 text-brand-green" /> {b}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`format-chips-${product.slug}`}>
          <span className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-bold text-slate-500">Age {product.ages}</span>
          <span className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-bold text-slate-500">Digital PDF</span>
          <span className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-bold text-slate-500">Printable</span>
        </div>
          <div className="mt-5 flex items-center justify-between gap-3">
          {product.available === false ? (
            <span
              data-testid={`coming-soon-${product.slug}`}
              className="rounded-full bg-slate-100 px-6 py-2.5 font-display text-base font-bold text-slate-400"
            >
              Coming Soon
            </span>
          ) : (
            <>
              <p className="flex items-center font-display text-3xl font-black text-slate-800">
                <IndianRupee className="h-6 w-6" />
                {product.price}
              </p>
              <div className="flex gap-2">
                {product.preview_images?.length > 0 && (
                  <button
                    data-testid={`preview-${product.slug}`}
                    onClick={() => setPreviewOpen(true)}
                    className="flex items-center gap-1.5 rounded-full border-2 border-slate-200 bg-white px-4 py-2.5 font-display text-sm font-bold text-slate-600 transition-colors hover:border-sky-300 hover:text-brand-blue"
                  >
                    <Eye className="h-4 w-4" /> Preview
                  </button>
                )}
                <button
                  data-testid={`buy-now-${product.slug}`}
                  onClick={() =>
                    product.payment_link
                      ? window.open(product.payment_link, "_blank", "noopener")
                      : onBuy(product)
                  }
                  className="whitespace-nowrap rounded-full px-6 py-2.5 font-display text-base font-bold text-white transition-transform active:translate-y-1 active:shadow-none"
                  style={{ backgroundColor: product.accent, boxShadow: `0 4px 0 0 ${product.accent}99` }}
                >
                  Buy Now
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <PreviewModal product={product} open={previewOpen} onClose={() => setPreviewOpen(false)} />
    </motion.div>
  );
};
